import type { DealListItem, OrderListItem, SupplierRoundSummary } from "@dishour/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toThousandths } from "../lib/quantity.js";
import { unwrap } from "./client.js";
import { invoicesKey } from "./invoices.js";

export const supplierDashboardKey = ["supplier", "dashboard"] as const;

export type SupplierDashboard = { rounds: SupplierRoundSummary[]; deals: DealListItem[] };

const get = <T,>(path: string) => fetch(path, { credentials: "same-origin" }).then(unwrap<T>);
const post = <T,>(path: string, body?: unknown) =>
  fetch(path, {
    method: "POST",
    credentials: "same-origin",
    headers: body === undefined ? {} : { "content-type": "application/json" },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  }).then(unwrap<T>);

/**
 * Parses a decimal price string ("4.20") into an integer count of cents without ever
 * multiplying a float — `Number(unitPrice) * 100` can land a cent off (e.g. 0.1 * 100 ===
 * 10.000000000000002 in IEEE 754), and a supplier deciding whether to confirm a round off a
 * number that drifted from the real price is a credibility problem, not a rounding footnote.
 */
function priceCentsOf(unitPrice: string): number {
  const [whole = "0", frac = ""] = unitPrice.split(".");
  return Number(whole) * 100 + Number((frac + "00").slice(0, 2) || "0");
}

/**
 * Display-only estimated amount, mirroring the server's `domain/quantity.ts` `mulQtyPrice`:
 * quantity thousandths × price cents, both integers, rounded once at the very end. The amount
 * actually invoiced is always whatever the server computes — this is only ever a preview.
 */
export function estimatedRevenue(qty: string, unitPrice: string): string {
  const cents = Math.round((toThousandths(qty) * priceCentsOf(unitPrice)) / 1000);
  return (cents / 100).toFixed(2);
}

export const useSupplierDashboard = () =>
  useQuery({ queryKey: supplierDashboardKey, queryFn: () => get<SupplierDashboard>("/api/supplier/dashboard") });

function useRoundAction(action: "confirm" | "cancel") {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (roundId: string) => post<SupplierRoundSummary>(`/api/supplier/rounds/${roundId}/${action}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: supplierDashboardKey }),
  });
}

export const useConfirmRound = () => useRoundAction("confirm");
export const useCancelRound = () => useRoundAction("cancel");

export const supplierOrdersKey = ["supplier", "orders"] as const;

export const useSupplierOrders = () =>
  useQuery({ queryKey: supplierOrdersKey, queryFn: () => get<OrderListItem[]>("/api/supplier/orders") });

function useSupplierOrderMutation<TVars>(fn: (vars: TVars) => Promise<OrderListItem>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (updated) =>
      queryClient.setQueryData<OrderListItem[]>(supplierOrdersKey, (prev) =>
        prev?.map((o) => (o.id === updated.id ? updated : o)),
      ),
  });
}

export const useAcceptOrder = () =>
  useSupplierOrderMutation((orderId: string) => post<OrderListItem>(`/api/supplier/orders/${orderId}/accept`));

export const useRejectOrder = () =>
  useSupplierOrderMutation((vars: { id: string; reason: string | null }) =>
    post<OrderListItem>(`/api/supplier/orders/${vars.id}/reject`, { reason: vars.reason }),
  );

export function useFinalizeOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; lines: { orderLineId: string; fulfilledQty: string }[] }) =>
      post<{ orderId: string; invoiceId: string | null }>(`/api/supplier/orders/${vars.id}/finalize`, {
        lines: vars.lines,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supplierOrdersKey });
      queryClient.invalidateQueries({ queryKey: invoicesKey("supplier") });
    },
  });
}
